import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

const ACTIVE_CUSTOMER_COMPANY_KEY = "activeCustomerCompany";
export const ACTIVE_CUSTOMER_COMPANY_EVENT = "customer-active-company-changed";

const normalizeString = (value) => (value || "").toString().trim();

export const getCustomerMembershipAccountIds = (membership = null, profile = {}) => {
  const accountIds = []
    .concat(Array.isArray(membership?.accountIds) ? membership.accountIds : [])
    .concat(membership?.primaryAccountId || [])
    .concat(membership?.accountId || [])
    .concat(profile?.primaryAccountId || [])
    .concat(profile?.accountId || [])
    .map((value) => normalizeString(value))
    .filter(Boolean);

  return Array.from(new Set(accountIds));
};

const sortMemberships = (memberships = []) =>
  [...memberships].sort((a, b) => {
    const aDefault = a?.isDefault ? 1 : 0;
    const bDefault = b?.isDefault ? 1 : 0;
    if (aDefault !== bDefault) return bDefault - aDefault;

    const aName = normalizeString(a?.companyName).toLowerCase();
    const bName = normalizeString(b?.companyName).toLowerCase();
    return aName.localeCompare(bName);
  });

export const getStoredActiveCustomerCompany = (uid) => {
  if (!uid || typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(ACTIVE_CUSTOMER_COMPANY_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (parsed?.uid !== uid) return "";
    return normalizeString(parsed.companyId);
  } catch (err) {
    console.warn("[customerMemberships] Failed to read active company:", err);
    return "";
  }
};

export const setStoredActiveCustomerCompany = ({ uid, companyId }) => {
  if (!uid || !companyId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      ACTIVE_CUSTOMER_COMPANY_KEY,
      JSON.stringify({
        uid,
        companyId,
        updatedAt: Date.now(),
      })
    );
    window.dispatchEvent(
      new CustomEvent(ACTIVE_CUSTOMER_COMPANY_EVENT, {
        detail: { uid, companyId },
      })
    );
  } catch (err) {
    console.warn("[customerMemberships] Failed to store active company:", err);
  }
};

export const clearStoredActiveCustomerCompany = (uid) => {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(ACTIVE_CUSTOMER_COMPANY_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!uid || parsed?.uid === uid) {
      window.localStorage.removeItem(ACTIVE_CUSTOMER_COMPANY_KEY);
    }
  } catch (err) {
    console.warn("[customerMemberships] Failed to clear active company:", err);
  }
};

export const loadCustomerMemberships = async (db, uid) => {
  if (!db || !uid) return [];
  const snap = await getDocs(collection(db, "users", uid, "companyMemberships"));
  return sortMemberships(
    snap.docs.map((membershipDoc) => ({
      id: membershipDoc.id,
      ...membershipDoc.data(),
      companyId: membershipDoc.id,
    }))
  );
};

export const loadCompanyCustomerMemberships = async (db, companyId) => {
  if (!db || !companyId) return [];
  const snap = await getDocs(
    query(
      collectionGroup(db, "companyMemberships"),
      where("companyId", "==", companyId),
      where("role", "==", "customer")
    )
  );

  return snap.docs.map((membershipDoc) => {
    const uid = membershipDoc.ref.parent.parent?.id || "";
    return {
      id: membershipDoc.id,
      uid,
      userId: uid,
      ...membershipDoc.data(),
      companyId,
    };
  });
};

export const buildMembershipAccountCounts = (memberships = []) => {
  const next = new Map();
  memberships.forEach((membership) => {
    const status = normalizeString(membership?.status).toLowerCase();
    if (status && status !== "active") return;

    const accountIds = Array.isArray(membership?.accountIds)
      ? membership.accountIds
      : membership?.accountId
      ? [membership.accountId]
      : [];

    Array.from(new Set(accountIds.map((value) => normalizeString(value)).filter(Boolean))).forEach((accountId) => {
      next.set(accountId, (next.get(accountId) || 0) + 1);
    });
  });
  return next;
};

export const resolveActiveCustomerMembership = ({ uid, profile = {}, memberships = [] }) => {
  const sorted = sortMemberships(memberships);
  const legacyCompanyId = normalizeString(profile.companyId);
  const storedActiveCompanyId = getStoredActiveCustomerCompany(uid);

  const matchingStored = sorted.find((membership) => membership.companyId === storedActiveCompanyId);
  const matchingLegacy = sorted.find((membership) => membership.companyId === legacyCompanyId);
  const defaultMembership = sorted.find((membership) => membership.isDefault);

  return matchingStored || matchingLegacy || defaultMembership || sorted[0] || null;
};

export const hydrateCustomerProfileWithMemberships = ({ uid, profile = {}, memberships = [] }) => {
  const role = normalizeString(profile.role).toLowerCase();
  if (role !== "customer") {
    return {
      ...profile,
      customerMemberships: [],
      activeCustomerMembership: null,
    };
  }

  const activeMembership = resolveActiveCustomerMembership({ uid, profile, memberships });
  const accountIds = getCustomerMembershipAccountIds(activeMembership, profile);

  return {
    ...profile,
    companyId: activeMembership?.companyId || profile.companyId || null,
    companyName: activeMembership?.companyName || profile.companyName || null,
    accountId: activeMembership?.primaryAccountId || activeMembership?.accountId || profile.accountId || null,
    primaryAccountId:
      activeMembership?.primaryAccountId || activeMembership?.accountId || profile.primaryAccountId || profile.accountId || null,
    repUserId: activeMembership?.repUserId || profile.repUserId || null,
    customerMemberships: memberships,
    activeCustomerMembership: activeMembership,
    customerAccountIds: accountIds,
  };
};

export const upsertCustomerMembership = async ({
  db,
  uid,
  companyId,
  companyName = "",
  accountId = "",
  accountName = "",
  accountIds = [],
  repUserId = null,
  role = "customer",
  isDefault = false,
  status = "Active",
  metadata = {},
}) => {
  if (!db || !uid || !companyId) return;

  const normalizedPrimaryAccountId = normalizeString(accountId);
  const mergedAccountIds = Array.from(
    new Set(
      [...(Array.isArray(accountIds) ? accountIds : []), normalizedPrimaryAccountId]
        .map((value) => normalizeString(value))
        .filter(Boolean)
    )
  );

  const membershipRef = doc(db, "users", uid, "companyMemberships", companyId);
  const existingSnap = await getDoc(membershipRef);
  const existing = existingSnap.exists() ? existingSnap.data() || {} : {};

  await setDoc(
    membershipRef,
    {
      companyId,
      companyName: normalizeString(companyName) || existing.companyName || "",
      role,
      status,
      accountId: normalizedPrimaryAccountId || existing.accountId || null,
      primaryAccountId: normalizedPrimaryAccountId || existing.primaryAccountId || existing.accountId || null,
      accountName: normalizeString(accountName) || existing.accountName || "",
      accountIds: Array.from(new Set([...(existing.accountIds || []), ...mergedAccountIds])),
      repUserId: normalizeString(repUserId) || existing.repUserId || null,
      isDefault: existing.isDefault === true ? true : !!isDefault,
      updatedAt: serverTimestamp(),
      ...metadata,
      ...(existingSnap.exists() ? {} : { createdAt: serverTimestamp() }),
    },
    { merge: true }
  );
};
